// App.js
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from './contexts/themeContext';
import { useAgentContext } from './hooks/useAgentContext';
import { AuthProvider, useAuth } from './providers/AuthProvider';
import { ToastProvider } from './providers/ToastProvider';
import { useLocation } from 'react-router-dom';
import './App.css';
import { CartProvider } from "./contexts/CartContext";
import React from 'react';

// Import Layout
import Layout from './components/layout/Layout';

// Import screens
import Homepage from './screens/homepage';
import Login from './screens/Login';
import Register from './screens/Register';
import RegistrationPaymentBanner from './components/RegistrationPaymentBanner';
import CompletePaymentModal from './components/CompletePaymentModal';
import ActivateRegistration from './screens/ActivateRegistration';
import BecomeAgent from './screens/BecomeAgent';
import DataScreen from './screens/data/DataScreen';
import DataPurchase from './screens/data/DataPurchase';
import SubAgentDashboard from './screens/subagent/SubAgentDashboard';
import AgentDashboard from './screens/agent/AgentDashboard';
import AdminDashboard from './screens/admin/Admindashboard';
import AdminUsers from './screens/admin/AdminUsers';
import AdminUserDetails from './screens/admin/AdminUserDetails';
import AdminPackages from './screens/admin/AdminPackages';
import SubAgentRegistration from './screens/subagent/SubAgentRegister';
import OrdersList from './screens/OrderList';
import ComingSoon from './screens/ComingSoon';
import SubAgentDisplay from './screens/agent/SubAgentDisplay';
import SubAgentDetails from './screens/agent/SubAgentDetails';
import AdminNotices from './screens/admin/AdminNotices';
import AdminWithdrawals from './screens/admin/AdminWithdrawals';
import Withdrawals from './screens/agent/Withdrawals';
import Notices from './screens/Notices';
import MarketplaceScreen from './screens/marketplace/MarketplaceScreen';
import CartScreen from './screens/marketplace/CartScreen';
import ProductDetail from './screens/marketplace/ProductDetail';

// Marketplace management (split pages)
import MarketplaceOverview from './screens/marketplace/MarketplaceOverview';
import MarketplaceCategories from './screens/marketplace/MarketplaceCategories';
import MarketplaceProducts from './screens/marketplace/MarketplaceProducts';
import MarketplaceCampaigns from './screens/marketplace/MarketplaceCampaigns';
import MarketplacePricing from './screens/marketplace/MarketplacePricing';
import MarketplaceOrders from './screens/marketplace/MarketplaceOrders';
import PreOrderCampaigns from "./screens/marketplace/PreOrderCampaigns";
import PreOrderCampaignDetail from "./screens/marketplace/PreOrderCampaignDetail";
import AdminOrderFulfillment from "./screens/marketplace/AdminOrderFulfillment";
import OrderDetail from "./screens/marketplace/OrderDetail";
import AdminOrderDetail from "./screens/marketplace/AdminOrderDetail";

import CourseList from "./screens/marketplace/CourseList";
import CourseDetail from "./screens/marketplace/CourseDetail";
import CourseEnrollmentCallback from "./screens/marketplace/CourseEnrollmentCallback";
import MyEnrollments from "./screens/marketplace/MyEnrollments";
import CoursesOverview from "./screens/marketplace/CoursesOverview";
import AdminCourses from "./screens/marketplace/AdminCourses";
import AdminCourseEnrollments from "./screens/marketplace/AdminCourseEnrollments";
import CoursePricing from "./screens/marketplace/CoursePricing";

// ── Route guards ──────────────────────────────────────────
const ProtectedRoute = ({ children, requiredRole, redirectTo = '/login' }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) return <FullPageLoader label="Verifying authentication…" />;
  if (!isAuthenticated) return <Navigate to={redirectTo} replace />;
  if (requiredRole && user?.role !== requiredRole) {
    return <Navigate to="/" replace />;
  }
  return children;
};

// Guard for pages that accept multiple roles
const ProtectedRouteAny = ({ children, roles = [] }) => {
  const { isAuthenticated, loading, user } = useAuth();
  if (loading) return <FullPageLoader label="Verifying authentication…" />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (roles.length && !roles.includes(user?.role)) {
    return <Navigate to="/" replace />;
  }
  return children;
};

// ── Full-page loader (theme-aware via CSS vars) ───────────
const FullPageLoader = ({ label = 'Loading…' }) => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '60vh',
      gap: 14,
      color: 'var(--secondaryText)',
      fontSize: 13,
    }}
  >
    <div className="app-spinner" />
    <span>{label}</span>
  </div>
);

// ── Layout wrapper ────────────────────────────────────────
// const AppLayout = ({ children }) => {
//   const location = useLocation();
//   const noLayoutPaths = ['/login', '/register'];
//   const shouldShowLayout = !noLayoutPaths.includes(location.pathname);

//   return shouldShowLayout ? <Layout>{children}</Layout> : children;
// };

const AppLayout = ({ children }) => {
  const location = useLocation();
  const { isAuthenticated, user, refreshUser } = useAuth();

  // Refresh the user once whenever the route changes and the user is
  // still flagged as an unpaid agent. This picks up the paid state
  // immediately after the backend redirects them back from Paystack.
  React.useEffect(() => {
    if (isAuthenticated && user?.is_unpaid_agent) {
      refreshUser();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const noLayoutPaths = [
    '/login',
    '/register',
    '/become-agent',
    '/register/payment',
    '/activate-registration',
  ];
  const shouldShowLayout = !noLayoutPaths.includes(location.pathname);

  if (!shouldShowLayout) return children;

  return (
    <Layout>
      <RegistrationPaymentBanner />
      {children}
    </Layout>
  );
};


// ── Subdomain validation ──────────────────────────────────
const AppContent = ({ children }) => {
  const { loading, isValidSubdomain } = useAgentContext();
  if (loading) return <FullPageLoader label="Validating store…" />;
  if (!isValidSubdomain) return null;
  return children;
};

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <ToastProvider>
            <CompletePaymentModal />
            <CartProvider>
              <AppContent>
                <AppLayout>
                  <Routes>
                    {/* ── Public ──────────────────────────────── */}
                    <Route path="/" element={<Homepage />} />
                    <Route path="/marketplace" element={<MarketplaceScreen />} />
                    <Route path="/marketplace/products/:id" element={<ProductDetail />} />
                    <Route path="/data" element={<DataScreen />} />
                    <Route path="/data/purchase/:id" element={<DataPurchase />} />
                    <Route path="/dropshipping" element={<MarketplaceScreen defaultReadyOnly />} />
                    <Route path="/preorder" element={<PreOrderCampaigns />} />
                    <Route path="/preorder/campaigns/:id" element={<PreOrderCampaignDetail />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/register" element={<Register />} />
                    <Route path="/subagent/register" element={<SubAgentRegistration />} />
                    <Route path="/notices" element={<Notices />} />

                    <Route path="/courses" element={<CourseList />} />
                    <Route path="/courses/:id" element={<CourseDetail />} />

                    {/* ── Marketplace management ──────────────── */}
                    {/* Hub: overview page showing all sections */}
                    <Route
                      path="/marketplace/manage"
                      element={
                        <ProtectedRouteAny roles={['ADMIN', 'AGENT', 'SUB_AGENT']}>
                          <MarketplaceOverview />
                        </ProtectedRouteAny>
                      }
                    />
                    <Route
                      path="/marketplace/manage/categories"
                      element={
                        <ProtectedRoute requiredRole="ADMIN">
                          <MarketplaceCategories />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/marketplace/manage/products"
                      element={
                        <ProtectedRoute requiredRole="ADMIN">
                          <MarketplaceProducts />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/marketplace/manage/campaigns"
                      element={
                        <ProtectedRoute requiredRole="ADMIN">
                          <MarketplaceCampaigns />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/marketplace/manage/pricing"
                      element={
                        <ProtectedRouteAny roles={["ADMIN", "AGENT", "SUB_AGENT"]}>
                          <MarketplacePricing />
                        </ProtectedRouteAny>
                      }
                    />

                    <Route
                      path="/become-agent"
                      element={
                        <ProtectedRouteAny roles={['CUSTOMER']}>
                          <BecomeAgent />
                        </ProtectedRouteAny>
                      }
                    />
                    <Route path="/activate-registration" element={<ActivateRegistration />} />
                    <Route
                      path="/register/payment"
                      element={
                        <ProtectedRouteAny roles={['AGENT', 'SUB_AGENT']}>
                          <BecomeAgent mode="payment" />
                        </ProtectedRouteAny>
                      }
                    />


                    {/* ── Customer-facing protected ───────────── */}
                    <Route
                      path="/cart"
                      element={
                        <ProtectedRoute>
                          <CartScreen />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/marketplace/orders"
                      element={
                        <ProtectedRoute>
                          <MarketplaceOrders />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/orders"
                      element={
                        <ProtectedRoute>
                          <OrdersList />
                        </ProtectedRoute>
                      }
                    />

                    {/* ── Admin ───────────────────────────────── */}
                    <Route
                      path="/admin/dashboard"
                      element={
                        <ProtectedRoute requiredRole="ADMIN">
                          <AdminDashboard />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/users"
                      element={
                        <ProtectedRoute requiredRole="ADMIN">
                          <AdminUsers />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/users/:userId"
                      element={
                        <ProtectedRoute requiredRole="ADMIN">
                          <AdminUserDetails />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/data-packages"
                      element={
                        <ProtectedRoute requiredRole="ADMIN">
                          <AdminPackages />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/notices"
                      element={
                        <ProtectedRoute requiredRole="ADMIN">
                          <AdminNotices />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/withdrawals"
                      element={
                        <ProtectedRoute requiredRole="ADMIN">
                          <AdminWithdrawals />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/marketplace/manage/orders/:id"
                      element={
                        <ProtectedRoute requiredRole="ADMIN">
                          <AdminOrderDetail />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/marketplace/manage/orders"
                      element={
                        <ProtectedRoute requiredRole="ADMIN">
                          <AdminOrderFulfillment />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/marketplace/orders/:id"
                      element={
                        <ProtectedRoute>
                          <OrderDetail />
                        </ProtectedRoute>
                      }
                    />

                    {/* ── Agent ───────────────────────────────── */}
                    <Route
                      path="/agent/dashboard"
                      element={
                        <ProtectedRoute requiredRole="AGENT">
                          <AgentDashboard />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/agent/subagents"
                      element={
                        <ProtectedRoute requiredRole="AGENT">
                          <SubAgentDisplay />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/agent/subagents/:subAgentId"
                      element={
                        <ProtectedRoute requiredRole="AGENT">
                          <SubAgentDetails />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/agent/withdrawals"
                      element={
                        <ProtectedRoute requiredRole="AGENT">
                          <Withdrawals />
                        </ProtectedRoute>
                      }
                    />

                    {/* ── Sub-agent ───────────────────────────── */}
                    <Route
                      path="/subagent/dashboard"
                      element={
                        <ProtectedRoute requiredRole="SUB_AGENT">
                          <SubAgentDashboard />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/subagent/withdrawals"
                      element={
                        <ProtectedRoute requiredRole="SUB_AGENT">
                          <Withdrawals />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/courses/enrollment/callback"
                      element={
                        <ProtectedRoute>
                          <CourseEnrollmentCallback />
                        </ProtectedRoute>
                      }
                    />

                    {/* Buyer's enrollments — needs auth */}
                    <Route
                      path="/courses/enrollments"
                      element={
                        <ProtectedRoute>
                          <MyEnrollments />
                        </ProtectedRoute>
                      }
                    />

                    {/* Courses management — role-gated */}
                    <Route
                      path="/courses/manage"
                      element={
                        <ProtectedRouteAny roles={["ADMIN", "AGENT", "SUB_AGENT"]}>
                          <CoursesOverview />
                        </ProtectedRouteAny>
                      }
                    />
                    <Route
                      path="/courses/manage/catalog"
                      element={
                        <ProtectedRoute requiredRole="ADMIN">
                          <AdminCourses />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/courses/manage/enrollments"
                      element={
                        <ProtectedRoute requiredRole="ADMIN">
                          <AdminCourseEnrollments />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/courses/manage/pricing"
                      element={
                        <ProtectedRouteAny roles={["ADMIN", "AGENT", "SUB_AGENT"]}>
                          <CoursePricing />
                        </ProtectedRouteAny>
                      }
                    />

                    {/* ── Misc ────────────────────────────────── */}
                    <Route path="/coming-soon" element={<ComingSoon />} />

                    {/* Catch-all */}
                    <Route path="*" element={<Navigate to="/coming-soon" replace />} />
                  </Routes>
                </AppLayout>
              </AppContent>
            </CartProvider>
          </ToastProvider>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;